export type AuditSnapshotKind = 'property_audit' | 'project_analytics'

export type MetricFormat =
    | 'number'
    | 'percent'
    | 'currency'
    | 'decimal'
    | 'health_status'

export interface MetricDefinition {
    key: string
    label: string
    format: MetricFormat
}

export const HEALTH_STATUS_BUCKETS = [
    {
        key: 'health_critical',
        label: 'Critical',
        shortLabel: 'Crit',
        color: '#E53935',
    },
    {
        key: 'health_poor',
        label: 'Poor',
        shortLabel: 'Poor',
        color: '#FB8C00',
    },
    {
        key: 'health_fair',
        label: 'Fair',
        shortLabel: 'Fair',
        color: '#FDD835',
    },
    {
        key: 'health_good',
        label: 'Good',
        shortLabel: 'Good',
        color: '#7CB342',
    },
    {
        key: 'health_excellent',
        label: 'Excellent',
        shortLabel: 'Exc',
        color: '#43A047',
    },
] as const

export type HealthStatusMetricKey = typeof HEALTH_STATUS_BUCKETS[number]['key']

export const PROPERTY_METRICS: MetricDefinition[] = [
    { key: 'total_apartments', label: 'Total Apartments', format: 'number' },
    { key: 'avg_health_score', label: 'Avg Health Score', format: 'decimal' },
    { key: 'health_status', label: 'Health Status', format: 'health_status' },
    {
        key: 'properties_with_0_vendors',
        label: 'Properties with 0 Vendors',
        format: 'number',
    },
    {
        key: 'properties_with_0_damage_types',
        label: 'Properties with 0 Damage Types',
        format: 'number',
    },
    {
        key: 'total_damages_last_30d',
        label: 'Total Damages (Last 30d)',
        format: 'number',
    },
    {
        key: 'properties_with_damages_last_30d',
        label: 'Properties w/ Damages Last 30d',
        format: 'number',
    },
    {
        key: 'properties_that_havent_synced_leases',
        label: "Properties That Haven't Synced Leases",
        format: 'number',
    },
    {
        key: 'properties_with_no_active_schedule',
        label: 'Properties with No Active Schedule',
        format: 'number',
    },
    {
        key: 'total_move_out_inspections',
        label: 'Total Move-Out Inspections',
        format: 'number',
    },
    {
        key: 'total_complete_inspection_units',
        label: 'Total Complete Inspection Units',
        format: 'number',
    },
    {
        key: 'total_scheduled_inspection_units',
        label: 'Total Scheduled Inspection Units',
        format: 'number',
    },
    {
        key: 'total_active_schedules',
        label: 'Total Active Schedules',
        format: 'number',
    },
    {
        key: 'active_schedule_pct',
        label: 'Active Schedule %',
        format: 'percent',
    },
    {
        key: 'total_scheduled_workorders',
        label: 'Total Scheduled Workorders',
        format: 'number',
    },
    {
        key: 'total_monthly_requests',
        label: 'Total Monthly Requests',
        format: 'number',
    },
]

export const PROJECT_METRICS: MetricDefinition[] = [
    { key: 'total_projects', label: 'Total Projects', format: 'number' },
    { key: 'total_workorders', label: 'Total Workorders', format: 'number' },
    { key: 'total_behind', label: 'Total Behind', format: 'number' },
    { key: 'behind_pct', label: '% Behind', format: 'percent' },
    { key: 'not_started', label: 'Not Started', format: 'number' },
    { key: 'in_progress', label: 'In Progress', format: 'number' },
    { key: 'complete', label: 'Complete', format: 'number' },
    { key: 'approved', label: 'Approved', format: 'number' },
    { key: 'invoiced', label: 'Invoiced', format: 'number' },
    { key: 'cai', label: 'CAI', format: 'number' },
    { key: 'cai_pct', label: 'CAI %', format: 'percent' },
    { key: 'units_ready_pct', label: 'Units Ready %', format: 'percent' },
    { key: 'cost_per_unit', label: 'Cost per Unit', format: 'currency' },
    { key: 'cost_per_bed', label: 'Cost per Bed', format: 'currency' },
    { key: 'total_budget', label: 'Total Budget', format: 'currency' },
    { key: 'total_forecasted', label: 'Total Forecasted', format: 'currency' },
    { key: 'budget_variance', label: 'Budget Variance', format: 'currency' },
    {
        key: 'avg_forecasted_per_project',
        label: 'Avg Forecasted / Project',
        format: 'currency',
    },
]

export type ProjectMetricGroupId =
    | 'overview'
    | 'completion'
    | 'pipeline'
    | 'budget'

export type PropertyMetricGroupId =
    | 'overview'
    | 'setup_gaps'
    | 'damages'
    | 'schedules'
    | 'inspections'

export interface MetricGroup {
    id: string
    label: string
    metricKeys: string[]
}

export interface ProjectMetricGroup extends MetricGroup {
    id: ProjectMetricGroupId
}

export interface PropertyMetricGroup extends MetricGroup {
    id: PropertyMetricGroupId
}

/** Modal layout order for project snapshot detail. */
export const PROJECT_METRIC_GROUPS: ProjectMetricGroup[] = [
    {
        id: 'overview',
        label: 'Overview',
        metricKeys: ['total_projects', 'total_workorders'],
    },
    {
        id: 'completion',
        label: 'Completion',
        metricKeys: [
            'total_behind',
            'behind_pct',
            'cai',
            'cai_pct',
            'units_ready_pct',
        ],
    },
    {
        id: 'pipeline',
        label: 'Workorder Pipeline',
        metricKeys: [
            'not_started',
            'in_progress',
            'complete',
            'approved',
            'invoiced',
        ],
    },
    {
        id: 'budget',
        label: 'Budget & Cost',
        metricKeys: [
            'total_budget',
            'total_forecasted',
            'budget_variance',
            'avg_forecasted_per_project',
            'cost_per_unit',
            'cost_per_bed',
        ],
    },
]

/** Overlay layout order for property audit snapshot detail. */
export const PROPERTY_METRIC_GROUPS: PropertyMetricGroup[] = [
    {
        id: 'overview',
        label: 'Portfolio Health',
        metricKeys: [
            'total_apartments',
            'avg_health_score',
            'health_critical',
            'health_poor',
            'health_fair',
            'health_good',
            'health_excellent',
        ],
    },
    {
        id: 'setup_gaps',
        label: 'Configuration Gaps',
        metricKeys: [
            'properties_with_0_vendors',
            'properties_with_0_damage_types',
            'properties_that_havent_synced_leases',
            'properties_with_no_active_schedule',
        ],
    },
    {
        id: 'damages',
        label: 'Damages (30d)',
        metricKeys: [
            'total_damages_last_30d',
            'properties_with_damages_last_30d',
        ],
    },
    {
        id: 'schedules',
        label: 'Schedules & Activity',
        metricKeys: [
            'total_active_schedules',
            'active_schedule_pct',
            'total_scheduled_workorders',
            'total_monthly_requests',
        ],
    },
    {
        id: 'inspections',
        label: 'Inspections',
        metricKeys: [
            'total_move_out_inspections',
            'total_complete_inspection_units',
            'total_scheduled_inspection_units',
        ],
    },
]

export interface AuditSnapshotRow {
    id: string | number
    label: string
    created_at: string
    is_scheduled: boolean
    xlsx_url?: string | null
    has_detail?: boolean
    metrics: Record<string, number | null>
}

export interface ProjectSnapshotKpis {
    total_projects: number
    total_workorders: number
    total_behind: number
    behind_pct: number
    cai: number
    cai_pct: number
    units_ready_pct: number
    units_ready_count?: number
    units_ready_scope_count?: number
    total_budget: number
    total_forecasted: number
    budget_variance: number
    projects_with_behind?: number
    pending_change_orders?: number
    pending_change_order_cost?: number
}

export interface ProjectSnapshotServiceRow {
    category: string
    line_items: number
    total_workorders: number
    complete: number
    behind: number
    complete_pct: number
    total_budget: number | null
    forecasted_cost: number | null
    cost_per_unit: number | null
    cost_per_bed: number | null
}

export interface ProjectSnapshotDetail {
    id: string
    kind: 'project_analytics'
    label: string
    created_at: string | null
    is_scheduled: boolean
    detail: {
        kpis: ProjectSnapshotKpis
        services: ProjectSnapshotServiceRow[]
    }
}

export interface PropertySnapshotPropertyRow {
    company_id: number
    name: string
    xlsx_url?: string | null
    health_score?: number | null
    health_category?: string | null
    vendor_count?: number | null
    damage_type_count?: number | null
    damage_count?: number | null
    lease_sync_date?: string | null
    move_out_inspection_count?: number | null
    complete_units_count?: number | null
    total_units_count?: number | null
    schedule_count?: number | null
    total_workorders?: number | null
    scheduled_workorders?: number | null
    request_count?: number | null
}

export interface PropertySnapshotDetail {
    id: string
    kind: 'property_audit'
    label: string
    created_at: string | null
    is_scheduled: boolean
    detail: {
        properties: PropertySnapshotPropertyRow[]
    }
}

/** KPI keys that filter the property drill-down table when clicked.
 * Only count / gap / health-bucket KPIs — not portfolio sums or averages.
 */
export const PROPERTY_KPI_FILTER_LABELS: Record<string, string> = {
    health_critical: 'Critical health',
    health_poor: 'Poor health',
    health_fair: 'Fair health',
    health_good: 'Good health',
    health_excellent: 'Excellent health',
    properties_with_0_vendors: '0 vendors',
    properties_with_0_damage_types: '0 damage types',
    properties_that_havent_synced_leases: "Haven't synced leases",
    properties_with_no_active_schedule: 'No active schedule',
    properties_with_damages_last_30d: 'Has damages (30d)',
}

export const isPropertyKpiFilterable = (key: string): boolean =>
    key in PROPERTY_KPI_FILTER_LABELS

const VALID_HEALTH_CATEGORIES = new Set<string>(
    HEALTH_STATUS_BUCKETS.map((bucket) => bucket.label),
)

/** Mirrors backend resolve_health_category / health_category_for_score. */
export const healthCategoryForScore = (score: number): string => {
    if (score >= 90) {
        return 'Excellent'
    }
    if (score >= 80) {
        return 'Good'
    }
    if (score >= 70) {
        return 'Fair'
    }
    if (score >= 60) {
        return 'Poor'
    }
    return 'Critical'
}

export const resolvePropertyHealthCategory = (
    property: PropertySnapshotPropertyRow,
): string => {
    const category = property.health_category
    if (typeof category === 'string' && VALID_HEALTH_CATEGORIES.has(category)) {
        return category
    }
    return healthCategoryForScore(Number(property.health_score) || 0)
}

export const propertyMatchesKpiFilter = (
    property: PropertySnapshotPropertyRow,
    filterKey: string | null,
): boolean => {
    if (!filterKey) {
        return true
    }
    switch (filterKey) {
        case 'health_critical':
            return resolvePropertyHealthCategory(property) === 'Critical'
        case 'health_poor':
            return resolvePropertyHealthCategory(property) === 'Poor'
        case 'health_fair':
            return resolvePropertyHealthCategory(property) === 'Fair'
        case 'health_good':
            return resolvePropertyHealthCategory(property) === 'Good'
        case 'health_excellent':
            return resolvePropertyHealthCategory(property) === 'Excellent'
        case 'properties_with_0_vendors':
            return (property.vendor_count || 0) === 0
        case 'properties_with_0_damage_types':
            return (property.damage_type_count || 0) === 0
        case 'properties_that_havent_synced_leases':
            return !property.lease_sync_date
        case 'properties_with_no_active_schedule':
            return (property.schedule_count || 0) === 0
        case 'properties_with_damages_last_30d':
            return (property.damage_count || 0) > 0
        default:
            return true
    }
}

export const getHealthStatusCounts = (
    metrics: Record<string, number | null | undefined>,
): Record<HealthStatusMetricKey, number> => {
    const counts = {} as Record<HealthStatusMetricKey, number>
    for (const bucket of HEALTH_STATUS_BUCKETS) {
        const value = metrics[bucket.key]
        counts[bucket.key] =
            value === null || value === undefined || Number.isNaN(value)
                ? 0
                : Number(value)
    }
    return counts
}

export const formatMetricValue = (
    value: number | null | undefined,
    format: MetricFormat,
): string => {
    if (format === 'health_status') {
        return '—'
    }
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—'
    }
    switch (format) {
        case 'percent':
            return `${value.toFixed(1)}%`
        case 'currency':
            return value.toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
            })
        case 'decimal':
            return value.toLocaleString(undefined, {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
            })
        case 'number':
        default:
            return value.toLocaleString(undefined, {
                maximumFractionDigits: 0,
            })
    }
}

export const computeMetricDeltaPct = (
    current: number | null | undefined,
    previous: number | null | undefined,
): number | null => {
    if (
        current === null ||
        current === undefined ||
        previous === null ||
        previous === undefined ||
        previous === 0
    ) {
        return null
    }
    return ((current - previous) / Math.abs(previous)) * 100
}

export const computeMetricDeltaAbs = (
    current: number | null | undefined,
    previous: number | null | undefined,
): number | null => {
    if (
        current === null ||
        current === undefined ||
        previous === null ||
        previous === undefined
    ) {
        return null
    }
    return current - previous
}
